import { useMemo, useState } from "react";
import { Receipt, Plus, Trash2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import {
  useLossPayees,
  useDeleteLossPayee,
  type LossPayeeLink,
} from "@/lib/api";
import { LossPayeeDialog } from "./LossPayeeDialog";
import { useConfirm } from "@/components/ui/confirm-dialog";
import { formatDate } from "@/lib/formatters";
import { ASSET_TABLE_COLUMN_WIDTHS } from "./asset-helpers";
import type { Policy } from "@shared/schema";

const HEAD_CLASS =
  "px-6 py-3 text-xs uppercase tracking-wider text-muted-foreground";
const CELL_CLASS = "px-6 py-4";
const ACTIONS_CELL_CLASS = "py-4 pr-6 text-right";

interface Props {
  insuredId: string;
  policies?: Policy[];
  // CSR-804 — when set, narrow the rendered list to LPs whose coverageIds
  // reference this policy (composite id format `<policyId>:<coverageKey>`).
  policyId?: string;
  // CSR-804 — wrapper variant. "insured" (default) wraps in a Card to match
  // the Insured Assets tab; "policy" wraps in a flat <section> to match the
  // sibling sections on the Policy Assets tab (mirrors AdditionalInsuredsSection).
  variant?: "policy" | "insured";
}

// CSR-792 — Assets-tab Loss Payees section. The pending row shows the
// orange "PENDING ADDITIONAL INFORMATION" chip in place of the email
// (locked spec requirement: the prototype omits this; AMS must show it).
// CSR-804 — same component now backs both the Insured and Policy Assets tabs.
// Pass `policyId` + `variant="policy"` to render the Policy variant.
export function LossPayeesSection({
  insuredId,
  policies = [],
  policyId,
  variant = "insured",
}: Props) {
  const { toast } = useToast();
  const { confirm, confirmDialog } = useConfirm();
  const { data: links, isLoading } = useLossPayees(insuredId);
  const deleteMutation = useDeleteLossPayee(insuredId);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingLP, setEditingLP] = useState<LossPayeeLink | null>(null);

  const items = links ?? [];

  // CSR-804 — when scoped to a single policy, keep only LPs whose
  // coverageIds reference that policy. Composite id format is
  // `<policyId>:<coverageKey>` (see server/storage.ts:_expandLossPayeeCoverages).
  // This mirrors the SendCOIDialog policy filter pattern.
  const filteredItems = useMemo(() => {
    if (!policyId) return items;
    return items.filter((lp) =>
      (lp.coverageIds ?? []).some((cid) => cid.startsWith(`${policyId}:`)),
    );
  }, [items, policyId]);

  const handleDelete = async (e: React.MouseEvent, link: LossPayeeLink) => {
    e.stopPropagation();
    const ok = await confirm({
      title: "Remove Loss Payee",
      description: `Remove "${link.name}" as a Loss Payee?`,
      confirmLabel: "Remove",
      destructive: true,
    });
    if (!ok) return;

    try {
      await deleteMutation.mutateAsync(link.id);
      toast({ title: "Deleted", description: "Loss Payee removed." });
    } catch (err) {
      toast({
        title: "Error",
        description: (err as Error).message,
        variant: "destructive",
      });
    }
  };

  const handleRowClick = (link: LossPayeeLink) => {
    setEditingLP(link);
    setDialogOpen(true);
  };

  const handleAdd = () => {
    setEditingLP(null);
    setDialogOpen(true);
  };

  const addButton = (
    <Button
      variant="outline"
      size="sm"
      className="gap-1.5"
      onClick={handleAdd}
      data-testid="loss-payees-add"
    >
      <Plus className="w-4 h-4" /> Add
    </Button>
  );

  const header = (
    <div className="flex items-center justify-between gap-3 px-6 py-4 border-b border-border">
      <div className="flex items-center gap-2">
        <Receipt className="w-5 h-5 text-foreground" aria-hidden="true" />
        <h3 className="text-base font-semibold">Loss Payees</h3>
      </div>
      <div className="flex items-center gap-3">
        <span
          className="inline-flex items-center justify-center min-w-[1.75rem] h-7 px-2 rounded-full bg-muted text-xs font-medium text-muted-foreground"
          data-testid="loss-payees-count"
        >
          {filteredItems.length}
        </span>
        {addButton}
      </div>
    </div>
  );

  const tableContent = (
    <>
      {isLoading ? (
        <div className="px-6 py-10 text-center text-sm text-muted-foreground">
          Loading...
        </div>
      ) : filteredItems.length === 0 ? (
        <div className="px-6 py-10 text-center text-sm text-muted-foreground">
          No loss payees linked.
        </div>
      ) : (
        <Table className="table-fixed">
          <TableHeader>
            <TableRow>
              <TableHead className={`${HEAD_CLASS} ${ASSET_TABLE_COLUMN_WIDTHS[0]}`}>Name</TableHead>
              <TableHead className={`${HEAD_CLASS} ${ASSET_TABLE_COLUMN_WIDTHS[1]}`}>Email</TableHead>
              <TableHead className={`${HEAD_CLASS} ${ASSET_TABLE_COLUMN_WIDTHS[2]}`}>COIs Sent</TableHead>
              <TableHead className={`${HEAD_CLASS} ${ASSET_TABLE_COLUMN_WIDTHS[3]}`}>Last Sent</TableHead>
              <TableHead className={`${HEAD_CLASS} ${ASSET_TABLE_COLUMN_WIDTHS[4]}`}>Created At</TableHead>
              <TableHead className={`${HEAD_CLASS} ${ASSET_TABLE_COLUMN_WIDTHS[5]}`}>
                <span className="sr-only">Actions</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredItems.map((link) => (
              <TableRow
                key={link.id}
                className="cursor-pointer hover:bg-muted/50"
                onClick={() => handleRowClick(link)}
                data-testid={`loss-payee-row-${link.id}`}
              >
                <TableCell className={`${CELL_CLASS} font-medium`}>
                  {link.name}
                </TableCell>
                <TableCell className={`${CELL_CLASS} text-muted-foreground`}>
                  {link.pendingAdditionalInfo ? (
                    <span
                      className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase bg-amber-500/10 text-amber-700 dark:text-amber-400"
                      data-testid="loss-payee-pending-chip"
                    >
                      Pending Additional Information
                    </span>
                  ) : (
                    link.email || "—"
                  )}
                </TableCell>
                <TableCell className={CELL_CLASS}>
                  {link.pendingAdditionalInfo || link.coiCount === null ? (
                    // CSR-2212 — pending rows never had a count; a null one
                    // means the COI service could not be reached just now.
                    // Both read as "no number to show", never as zero.
                    <span
                      title={
                        link.coiCount === null && !link.pendingAdditionalInfo
                          ? "COI history is temporarily unavailable"
                          : undefined
                      }
                    >
                      —
                    </span>
                  ) : (
                    <Badge variant="outline" className="rounded-full">
                      {link.coiCount}
                    </Badge>
                  )}
                </TableCell>
                <TableCell className={`${CELL_CLASS} text-muted-foreground`}>
                  {link.pendingAdditionalInfo || link.coiCount === null
                    ? "—"
                    : link.lastSentAt
                      ? formatDate(link.lastSentAt)
                      : "Never"}
                </TableCell>
                <TableCell className={`${CELL_CLASS} text-muted-foreground`}>
                  {link.createdAt ? formatDate(link.createdAt) : "—"}
                </TableCell>
                <TableCell className={ACTIONS_CELL_CLASS}>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
                    onClick={(e) => handleDelete(e, link)}
                  >
                    <Trash2 className="w-4 h-4" />
                    <span className="sr-only">Delete</span>
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </>
  );

  const dialog = (
    <LossPayeeDialog
      open={dialogOpen}
      onOpenChange={(open) => {
        setDialogOpen(open);
        if (!open) setEditingLP(null);
      }}
      insuredId={insuredId}
      policies={policies}
      editData={editingLP}
    />
  );

  if (variant === "insured") {
    return (
      <>
        <Card className="overflow-hidden" data-testid="loss-payees-section">
          {header}
          {tableContent}
        </Card>
        {dialog}
        {confirmDialog}
      </>
    );
  }

  return (
    <>
      <section
        className="rounded-xl border border-border bg-card overflow-hidden"
        data-testid="policy-loss-payees"
      >
        {header}
        {tableContent}
      </section>
      {dialog}
      {confirmDialog}
    </>
  );
}
